import { useMemo } from 'react';

/**
 * StoryRing - Instagram tarzı logo etrafında story çerçevesi
 * - Story yokken: Halka gözükmemeli (direkt children render)
 * - İzlenmemiş story varken: Renkli gradient animasyonlu çerçeve
 * - Tümü izlendiyse: Gri çerçeve
 */
const StoryRing = ({ 
    children, 
    stories = [], 
    hasUnviewed = false,
    allViewed = false,
    onClick,
    size = 'default'
}) => {
    const hasStories = stories && stories.length > 0;
    
    // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
    
    // Check if there are unviewed stories
    const hasUnviewedStories = useMemo(() => {
        if (!hasStories) return false;
        if (hasUnviewed !== undefined) return hasUnviewed;
        return stories.some(s => !s.viewed);
    }, [hasStories, hasUnviewed, stories]);
    
    // Check if all stories are viewed
    const allStoriesViewed = useMemo(() => {
        if (!hasStories) return false;
        if (allViewed !== undefined) return allViewed;
        return stories.every(s => s.viewed);
    }, [hasStories, allViewed, stories]);

    // Size config
    const sizeConfig = useMemo(() => {
        switch (size) {
            case 'small': return { padding: 2, gap: 2 };
            case 'large': return { padding: 4, gap: 3 };
            default: return { padding: 3, gap: 2 };
        }
    }, [size]);

    // Ring color based on state
    const ringStyle = useMemo(() => {
        if (!hasStories) return { background: 'transparent' };
        if (hasUnviewedStories) {
            // Unviewed stories - colorful gradient with animation
            return {
                background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                backgroundSize: '200% 200%',
                animation: 'gradient-rotate 3s ease infinite'
            };
        }
        // All viewed - gray ring
        return {
            background: '#d1d5db' // gray-300
        };
    }, [hasStories, hasUnviewedStories]);

    // NO STORIES - No ring, just render children (AFTER all hooks)
    if (!hasStories) {
        return <div className="cursor-default">{children}</div>;
    }

    return (
        <button
            onClick={onClick}
            className="relative block focus:outline-none cursor-pointer"
        >
            {/* Gradient Ring */}
            <div 
                className="absolute inset-0 rounded-full"
                style={{
                    padding: sizeConfig.padding,
                    ...ringStyle,
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude'
                }}
            />

            {/* White gap between ring and content */}
            <div 
                className="relative rounded-full bg-white"
                style={{ padding: sizeConfig.gap }}
            >
                {/* Content (logo) */}
                <div className="rounded-full overflow-hidden">
                    {children}
                </div>
            </div>

            {/* Pulse animation for unviewed */}
            {hasUnviewedStories && (
                <div 
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{ animation: 'story-ring-pulse 2s ease-in-out infinite' }}
                />
            )}

            <style>{`
                @keyframes story-ring-pulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(236, 72, 153, 0.4); }
                    50% { box-shadow: 0 0 0 6px rgba(236, 72, 153, 0); }
                }
                @keyframes gradient-rotate {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
            `}</style>
        </button>
    );
};

export default StoryRing;
